import axios from "axios";
import { defineStore } from "pinia";
import { toRaw } from "vue";
import { useNuxtApp } from "#app/nuxt";
import { hasOrganizationPermission } from "~/utils/organizationPermissions";
import type {
  CreateAccountInput,
  LoginErrorResult,
  LoginResponse,
  LoginResult,
  RecaptchaInput,
  RecaptchaChallengeResponse,
  SchoolLoginContext,
  SocialAuthFlow,
  SocialAuthProvider,
  UserAccessCookie,
} from "~/types/auth";
import {
  clearAuthSession,
  clearViewAsStorage,
  getCurrentAuthSession,
  hasAccessToken,
  restorePrimaryAuthSession,
  setAuthTokens,
  setUserAccessCookie,
  waitForActiveRefresh,
} from "~/repository/authSession";
import { useCurrentSubscriptionStore } from "~/stores/currentSubscription";
import { identifyFullstoryUser } from "~/utils/fullstory";
import type {
  PlanAccessResponse,
  StudentAccessMode,
  StudentAccessResponse,
} from "~/types/profile";
import {
  canStudentAccessLearning,
  studentLearningAccessRedirect,
} from "~/utils/studentAccess";
import type {
  OrganizationBrandResponse,
  OrganizationContextResponse,
  OrganizationPreferencesResponse,
  OrganizationSummaryResponse,
} from "~/types/organizations";
import { buildOrganizationBrand } from "~/utils/schoolBrand";
import { safeAuthReturnPathForUser } from "~/utils/authRedirect";
import {
  canCorrectOfficialMaterials,
  getAdminDashboardEntryPath,
} from "~/utils/adminCapabilities";
import {
  schoolEntryPath,
  schoolTeacherEquivalent,
} from "~/utils/teacherExperience";
import { dispatchVideoCallIdentityWillChange } from "~/utils/videoCalls/identity";

const DEFAULT_LOGIN_ERROR_MESSAGE = "Não foi possível realizar login";
const NETWORK_LOGIN_ERROR_MESSAGE =
  "Não foi possível conectar ao servidor. Tente novamente em instantes.";
const RECAPTCHA_CHALLENGE_REQUIRED_CODE = "recaptcha_challenge_required";
const RECAPTCHA_CHALLENGE_FAILED_CODE = "recaptcha_challenge_failed";

// A logout or another identity change invalidates delayed authentication work.
let authMutationVersion = 0;

interface AdoptStudentSessionOptions {
  recordLogin?: boolean;
  expectedVersion?: number;
}

type OrganizationContextWithBrand = OrganizationContextResponse & {
  school_brand?: OrganizationBrandResponse | null;
};

const createDefaultUserState = () => ({
  id: 0,
  teacherId: null as number | null,
  studentId: null as number | null,
  firstName: "",
  lastName: "",
  email: "",
  birthDate: "",
  payment_information_updated: true,
  permissions: {
    isTeacher: false,
    isStudent: false,
    isStaff: false,
    isSuperuser: false,
    adminCapabilities: [] as string[],
    groups: [""],
  },
  userAccessConfig: {
    ads: false,
    defaultFreeUsers: false,
    group: 1,
    maxStudents: 5,
    teachersAccounts: 1,
    maxCustomLessons: 1,
  },
  planAccess: {
    source: "individual",
    organizationId: null,
    billingAccountStatus: null,
    hasActiveSubscription: false,
    isFreeTier: true,
    cycleStart: null,
    cycleEnd: null,
    limits: {},
    features: {},
    quotas: {},
  } as {
    source: "individual" | "organization";
    organizationId: number | null;
    billingAccountStatus: string | null;
    hasActiveSubscription: boolean;
    isFreeTier: boolean;
    cycleStart: string | null;
    cycleEnd: string | null;
    limits: PlanAccessResponse["limits"];
    features: PlanAccessResponse["features"];
    quotas: PlanAccessResponse["quotas"];
  },
  activeOrganization: null as OrganizationSummaryResponse | null,
  availableOrganizations: [] as OrganizationSummaryResponse[],
  schoolBrand: null as OrganizationBrandResponse | null,
  requiresOrganizationSelection: false,
  individualContextAllowed: true,
  featureFlags: {} as Record<string, boolean>,
  referralProgramAccess: false,
  documentAccess: false,
  studentAccess: {
    isBlocked: false,
    isArchived: false,
    canAccessLearning: undefined as boolean | undefined,
    mode: "regular" as StudentAccessMode,
    trialAccess: {
      current: null,
      next: null,
    } as NonNullable<StudentAccessResponse["trial_access"]>,
    allowedSections: [] as string[],
    schoolContact: null as {
      schoolName: string;
      email: string;
      phoneNumber: string;
    } | null,
  },
});

const resolveLoginError = (error: unknown): LoginErrorResult => {
  if (!axios.isAxiosError(error)) {
    return { error: true, detail: DEFAULT_LOGIN_ERROR_MESSAGE };
  }

  if (!error.response) {
    return { error: true, detail: NETWORK_LOGIN_ERROR_MESSAGE };
  }

  const responseData = error.response.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return { error: true, detail: responseData };
  }

  if (responseData && typeof responseData === "object") {
    const responseCode =
      "code" in responseData && typeof responseData.code === "string"
        ? responseData.code
        : undefined;
    const responseRecaptcha =
      "recaptcha" in responseData &&
      responseData.recaptcha &&
      typeof responseData.recaptcha === "object"
        ? (responseData.recaptcha as RecaptchaChallengeResponse)
        : undefined;

    if ("detail" in responseData && typeof responseData.detail === "string") {
      return {
        error: true,
        detail: responseData.detail,
        code: responseCode,
        recaptcha: responseRecaptcha,
      };
    }

    const firstFieldError = Object.values(responseData)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .find(
        (value: unknown): value is string =>
          typeof value === "string" && value.trim().length > 0,
      );

    if (firstFieldError) {
      return {
        error: true,
        detail: firstFieldError,
        code: responseCode,
        recaptcha: responseRecaptcha,
      };
    }

    if (
      "non_field_errors" in responseData &&
      Array.isArray(responseData.non_field_errors)
    ) {
      const firstError = responseData.non_field_errors.find(
        (value: unknown): value is string =>
          typeof value === "string" && value.trim().length > 0,
      );

      if (firstError) {
        return {
          error: true,
          detail: firstError,
          code: responseCode,
          recaptcha: responseRecaptcha,
        };
      }
    }
  }

  return { error: true, detail: DEFAULT_LOGIN_ERROR_MESSAGE };
};

const resolveLoginErrorMessage = (error: unknown): string =>
  resolveLoginError(error).detail;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: createDefaultUserState(),
    profileLoaded: false,
  }),
  getters: {
    defaultLoginDestination(state): string {
      if (state.user.permissions.isStudent)
        return canStudentAccessLearning(state.user.studentAccess)
          ? "/student-dashboard/home"
          : "/student-dashboard/requests";
      if (state.user.activeOrganization) return schoolEntryPath(state.user);
      if (state.user.availableOrganizations.length > 0)
        return "/school-dashboard";
      if (state.user.permissions.isTeacher) return "/dashboard/home";
      if (canCorrectOfficialMaterials(state.user.permissions.adminCapabilities))
        return "/admin-dashboard/content-quality";
      return getAdminDashboardEntryPath(state.user.permissions) || "/";
    },
    hasActiveCommercialAccess(state) {
      return state.user.planAccess.hasActiveSubscription;
    },
    needsPremiumUpsell(): boolean {
      return (
        this.user.permissions.isTeacher &&
        !this.user.activeOrganization &&
        !this.hasActiveCommercialAccess
      );
    },
    accessibleOrganizations(state) {
      return state.user.availableOrganizations.filter(
        (organization) => organization.can_access,
      );
    },
    hasIndividualDocumentsAccess(state): boolean {
      return (
        state.profileLoaded &&
        state.user.documentAccess &&
        !state.user.activeOrganization
      );
    },
    hasIndividualStudentRequestsAccess(state): boolean {
      return (
        state.profileLoaded &&
        state.user.permissions.isTeacher &&
        !state.user.activeOrganization &&
        state.user.individualContextAllowed &&
        state.user.planAccess.source === "individual" &&
        state.user.planAccess.hasActiveSubscription &&
        state.user.featureFlags.individual_teacher_requests === true
      );
    },
    hasSchoolDocumentsAccess(state): boolean {
      return (
        state.profileLoaded &&
        state.user.documentAccess &&
        Boolean(state.user.activeOrganization)
      );
    },
    hasSchoolManagementAccess(state) {
      return hasOrganizationPermission(
        state.user.activeOrganization,
        "organization.dashboard.access",
      );
    },
    requiresSchoolContext(state) {
      return (
        state.user.permissions.isTeacher &&
        !state.user.individualContextAllowed &&
        !state.user.activeOrganization
      );
    },
    canCorrectOfficialMaterials(state) {
      return state.user.permissions.adminCapabilities.includes(
        "content_quality.can_correct_official_materials",
      );
    },
  },
  actions: {
    applyOrganizationContext(context: OrganizationContextWithBrand) {
      this.user.documentAccess = false;
      this.user.activeOrganization = context.active_organization ?? null;
      this.user.availableOrganizations = context.available_organizations ?? [];
      this.user.requiresOrganizationSelection =
        context.requires_organization_selection ?? false;
      this.user.individualContextAllowed =
        context.individual_context_allowed ?? true;
      this.user.schoolBrand = Object.prototype.hasOwnProperty.call(
        context,
        "school_brand",
      )
        ? (context.school_brand ?? null)
        : buildOrganizationBrand(this.user.activeOrganization);
    },
    syncActiveOrganizationPreferences(
      preferences: OrganizationPreferencesResponse,
    ) {
      if (!this.user.activeOrganization) {
        return;
      }

      const activeOrganization = {
        ...this.user.activeOrganization,
        preferences,
      };
      this.user.activeOrganization = activeOrganization;
      this.user.availableOrganizations = this.user.availableOrganizations.map(
        (organization) =>
          organization.id === activeOrganization.id
            ? { ...organization, preferences }
            : organization,
      );
      this.user.schoolBrand = buildOrganizationBrand(activeOrganization);
    },
    async resolvePostLoginDestination(destination: string) {
      destination =
        safeAuthReturnPathForUser(
          destination,
          this.user.permissions,
          this.user.documentAccess,
        ) || this.defaultLoginDestination;
      if (this.user.permissions.isStudent) {
        return (
          studentLearningAccessRedirect(destination, {
            ...this.user.studentAccess,
            hasSchool: Boolean(this.user.studentAccess.schoolContact),
          }) || destination
        );
      }

      if (destination.startsWith("/school-invitations/")) {
        return destination;
      }

      if (this.user.activeOrganization) {
        return destination === "/dashboard/home"
          ? schoolEntryPath(this.user)
          : destination.startsWith("/dashboard/")
            ? schoolTeacherEquivalent(destination) || schoolEntryPath(this.user)
            : destination;
      }

      const accessibleOrganizations = this.accessibleOrganizations;
      if (accessibleOrganizations.length === 1) {
        await this.switchOrganizationContext(accessibleOrganizations[0].id);
        return destination === "/dashboard/home"
          ? schoolEntryPath(this.user)
          : destination.startsWith("/dashboard/")
            ? schoolTeacherEquivalent(destination) || schoolEntryPath(this.user)
            : destination;
      }

      if (
        this.user.requiresOrganizationSelection ||
        this.user.availableOrganizations.length > 0 ||
        !this.user.individualContextAllowed
      ) {
        return `/school-select?redirect=${encodeURIComponent(destination)}`;
      }

      return destination;
    },
    async login(
      email: string,
      password: string,
      recaptcha: RecaptchaInput,
      studentLogin = false,
      schoolContext?: SchoolLoginContext,
    ): Promise<LoginResult> {
      const attemptVersion = ++authMutationVersion;
      const { $api } = useNuxtApp();
      const credentials = {
        username: email,
        password,
        recaptcha,
        ...schoolContext,
      };
      const loginRequest =
        studentLogin && !schoolContext
          ? $api.auth.studentLogin(credentials)
          : $api.auth.login(credentials);
      const response = await loginRequest.catch((error): LoginErrorResult => {
        return resolveLoginError(error);
      });

      if (attemptVersion !== authMutationVersion) {
        return {
          error: true,
          detail: "O acesso foi interrompido. Entre novamente para continuar.",
        };
      }
      if ("error" in response) {
        const errorMapping: { [key: string]: string } = {
          "No active account found with the given credentials":
            "Email e/ou senha incorretos",
          "User does not exist": "Email e/ou senha incorretos",
          "Password is not correct": "Email e/ou senha incorretos",
          "User is not student": "Use uma conta de aluno para continuar.",
          [RECAPTCHA_CHALLENGE_REQUIRED_CODE]:
            "Precisamos confirmar que é você. Marque a caixa abaixo para continuar.",
          [RECAPTCHA_CHALLENGE_FAILED_CODE]:
            "Não foi possível confirmar a verificação. Tente novamente.",
        };

        return {
          error: true,
          detail: response.code
            ? errorMapping[response.code] || response.detail
            : errorMapping[response.detail] ||
              response.detail ||
              DEFAULT_LOGIN_ERROR_MESSAGE,
          code: response.code,
          recaptcha: response.recaptcha,
        };
      }

      if (studentLogin || schoolContext) {
        try {
          // School sessions replace the previous profile atomically too.
          await this.adoptStudentSession(response, {
            expectedVersion: attemptVersion,
          });
        } catch (error) {
          return resolveLoginError(error);
        }
      } else {
        setAuthTokens(response);
        await this.fetchUserData();
      }

      return schoolContext
        ? {
            error: false,
            school_access: response.school_access,
            next_path: response.next_path,
            organization_id: response.organization_id,
          }
        : { error: false };
    },
    async adoptStudentSession(
      tokens: LoginResponse,
      options: AdoptStudentSessionOptions = {},
    ): Promise<void> {
      const expectedVersion = options.expectedVersion ?? authMutationVersion;
      await waitForActiveRefresh();
      if (expectedVersion !== authMutationVersion)
        throw new Error("Authentication superseded");

      const previousSession = getCurrentAuthSession();
      const previousState = structuredClone(toRaw(this.$state));

      try {
        setAuthTokens(tokens);
        this.$patch({
          user: createDefaultUserState(),
          profileLoaded: false,
        });
        if (options.recordLogin) {
          const { $api } = useNuxtApp();
          await $api.auth.recordLogin();
        }
        await this.fetchUserData({
          suppressAuthFailureRedirect: true,
        });
        if (expectedVersion !== authMutationVersion)
          throw new Error("Authentication superseded");
      } catch (error) {
        if (expectedVersion !== authMutationVersion) throw error;
        if (previousSession) {
          restorePrimaryAuthSession(previousSession);
          this.$patch(previousState);
        } else {
          clearAuthSession();
          this.$patch({
            user: createDefaultUserState(),
            profileLoaded: false,
          });
        }

        throw error;
      }
    },
    async studentSignup(account: CreateAccountInput): Promise<LoginResult> {
      const { $api } = useNuxtApp();
      const response = await $api.auth
        .createStudentAccount(account)
        .catch((error): LoginErrorResult => resolveLoginError(error));

      if ("error" in response) {
        return response;
      }

      try {
        await this.adoptStudentSession(response);
      } catch (error) {
        return resolveLoginError(error);
      }

      return { error: false };
    },
    async studentSocialAuth(
      provider: SocialAuthProvider,
      flow: SocialAuthFlow,
      credential: string,
      recaptcha: RecaptchaInput,
    ): Promise<LoginResult> {
      const { $api } = useNuxtApp();
      const response = await $api.auth
        .studentSocialAuth(provider, {
          flow,
          credential,
          recaptcha,
        })
        .catch((error): LoginErrorResult => resolveLoginError(error));

      if ("error" in response) {
        return response;
      }

      try {
        await this.adoptStudentSession(response);
      } catch (error) {
        return resolveLoginError(error);
      }

      return { error: false };
    },
    async socialAuth(
      provider: SocialAuthProvider,
      flow: SocialAuthFlow,
      credential: string,
      recaptcha: RecaptchaInput,
    ): Promise<LoginResult> {
      const { $api } = useNuxtApp();
      const response = await $api.auth
        .socialAuth(provider, {
          flow,
          credential,
          recaptcha,
        })
        .catch((error): string => {
          return resolveLoginErrorMessage(error);
        });

      if (typeof response === "string") {
        return {
          error: true,
          detail: response || DEFAULT_LOGIN_ERROR_MESSAGE,
        };
      }

      setAuthTokens(response);
      await this.fetchUserData();

      return { error: false };
    },
    async fetchUserData(options?: { suppressAuthFailureRedirect?: boolean }) {
      const profileVersion = authMutationVersion;
      const { $api } = useNuxtApp();
      const { gtag } = useGtag();

      const meResponse = await $api.profile.fetchUserData({
        suppressAuthFailureRedirect:
          options?.suppressAuthFailureRedirect === true,
      });

      if (profileVersion !== authMutationVersion) return;
      this.user.id = meResponse.id;
      this.user.teacherId = meResponse.teacher_id;
      this.user.studentId = meResponse.student_id;
      this.user.firstName = meResponse.first_name;
      this.user.lastName = meResponse.last_name;
      this.user.email = meResponse.email;
      this.user.birthDate = meResponse.birth_date;
      this.user.permissions.isTeacher = meResponse.is_teacher;
      this.user.permissions.isStudent = meResponse.is_student;
      this.user.permissions.isStaff = meResponse.is_staff;
      this.user.permissions.isSuperuser = meResponse.is_superuser;
      this.user.permissions.adminCapabilities =
        meResponse.admin_capabilities ?? [];
      this.user.permissions.groups = meResponse.groups;
      this.user.userAccessConfig.ads = meResponse.user_access_config.ads;
      this.user.userAccessConfig.defaultFreeUsers =
        meResponse.user_access_config.default_free_users;
      this.user.userAccessConfig.group = meResponse.user_access_config.group;
      this.user.userAccessConfig.maxStudents =
        meResponse.user_access_config.max_students;
      this.user.userAccessConfig.teachersAccounts =
        meResponse.user_access_config.teachers_accounts;
      this.user.userAccessConfig.maxCustomLessons =
        meResponse.user_access_config.max_custom_lessons;
      this.user.payment_information_updated =
        meResponse.payment_information_updated;
      this.user.planAccess.source =
        meResponse.plan_access.source || "individual";
      this.user.planAccess.organizationId =
        meResponse.plan_access.organization_id ?? null;
      this.user.planAccess.billingAccountStatus =
        meResponse.plan_access.billing_account_status ?? null;
      this.user.planAccess.hasActiveSubscription =
        meResponse.plan_access.has_active_subscription;
      this.user.planAccess.isFreeTier = meResponse.plan_access.is_free_tier;
      this.user.planAccess.cycleStart = meResponse.plan_access.cycle_start;
      this.user.planAccess.cycleEnd = meResponse.plan_access.cycle_end;
      this.user.planAccess.limits = meResponse.plan_access.limits;
      this.user.planAccess.features = meResponse.plan_access.features;
      this.user.planAccess.quotas = meResponse.plan_access.quotas;
      this.applyOrganizationContext(meResponse);
      this.user.featureFlags = meResponse.feature_flags || {};
      this.user.documentAccess = meResponse.document_access === true;
      this.user.studentAccess = {
        isBlocked: meResponse.student_access?.is_blocked === true,
        isArchived: meResponse.student_access?.is_archived === true,
        canAccessLearning:
          meResponse.student_access?.can_access_learning ??
          meResponse.student_access?.is_blocked !== true,
        mode:
          meResponse.student_access?.mode ??
          (meResponse.student_access?.is_blocked ? "restricted" : "regular"),
        trialAccess: meResponse.student_access?.trial_access ?? {
          current: null,
          next: null,
        },
        allowedSections: meResponse.student_access?.allowed_sections || [],
        schoolContact: meResponse.student_access?.school_contact
          ? {
              schoolName: meResponse.student_access.school_contact.school_name,
              email: meResponse.student_access.school_contact.email,
              phoneNumber:
                meResponse.student_access.school_contact.phone_number,
            }
          : null,
      };
      this.user.referralProgramAccess =
        meResponse.referral_program_access ?? false;
      this.profileLoaded = true;
      identifyFullstoryUser(
        meResponse,
        meResponse.is_teacher
          ? {
              isSubscriber: meResponse.plan_access.has_active_subscription,
            }
          : undefined,
      );

      const userAccessCookie: UserAccessCookie = {
        isTeacher: meResponse.is_teacher,
        isStudent: meResponse.is_student,
        isStaff: meResponse.is_staff,
        isSuperuser: meResponse.is_superuser,
        adminCapabilities: meResponse.admin_capabilities ?? [],
      };

      setUserAccessCookie(userAccessCookie);

      gtag("set", { user_id: meResponse.id });
      gtag("set", "user_properties", {
        user_type: "registered",
        user_role: meResponse.is_teacher ? "teacher" : "student",
        ...(meResponse.is_teacher
          ? {
              teacher_subscription: meResponse.plan_access
                .has_active_subscription
                ? "active"
                : "inactive",
            }
          : {}),
      });
    },
    async switchOrganizationContext(organizationId: number | null) {
      const contextVersion = ++authMutationVersion;
      const { $api } = useNuxtApp();
      const response = await $api.organizations.switchContext({
        organization_id: organizationId,
      });
      if (contextVersion !== authMutationVersion)
        throw new Error("Authentication superseded");
      setAuthTokens(response);
      this.applyOrganizationContext(response);
      this.profileLoaded = false;
      useCurrentSubscriptionStore().clear();
      // Refresh all entitlements before navigation; the previous school's
      // commercial access must never remain the active UI snapshot.
      this.user.planAccess = {
        ...createDefaultUserState().planAccess,
        source: organizationId ? "organization" : "individual",
        organizationId,
      };
      this.user.userAccessConfig = {
        ...createDefaultUserState().userAccessConfig,
        defaultFreeUsers: true,
      };
      await this.fetchUserData();
      return response;
    },
    logout() {
      authMutationVersion += 1;
      const currentSubscriptionStore = useCurrentSubscriptionStore();

      dispatchVideoCallIdentityWillChange();
      clearAuthSession();
      clearViewAsStorage();
      currentSubscriptionStore.clear();
      this.$patch({
        user: createDefaultUserState(),
        profileLoaded: false,
      });
    },
    userLoggedIn() {
      return hasAccessToken();
    },
  },
});
